import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import CircularProgress from '@mui/material/CircularProgress';
import { useEffect, useState } from 'react';
import { CustomizationRichTextEditor, wysiwygEditorBoxSx } from '../chat/CustomizationRichTextEditor';

import axios from 'axios';
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';

export default function QAModal({ open, onClose, mode, qaData, onSave }) {
  const [question, setQuestion] = useState(qaData?.question || '');
  const [answer, setAnswer] = useState(qaData?.answer || '');
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(qaData?.agentId || '');
  const [showAllKbs, setShowAllKbs] = useState(false);
  const [kbOptions, setKbOptions] = useState([]);
  const [filteredKbOptions, setFilteredKbOptions] = useState([]);
  const [selectedKbIds, setSelectedKbIds] = useState(qaData?.kbIds || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setQuestion(qaData?.question || '');
    setAnswer(qaData?.answer || '');
    setSelectedAgentId(qaData?.agentId || '');
    setSelectedKbIds(qaData?.kbIds || []);
    setShowAllKbs(false);
  }, [qaData, open]);

  useEffect(() => {
    // Fetch agents on open
    if (open) {
      (async () => {
        try {
          const jwtToken = Cookies.getJwtToken();
          const res = await axios.post(`${API_URL}/agents/getAllAgents`, null, {
            headers: { Authorization: `Bearer ${jwtToken}` }
          });
          setAgents(res.data || []);
        } catch {
          setAgents([]);
        }
      })();
    }
  }, [open]);

  useEffect(() => {
    // Fetch KBs for selected agent or all KBs, or all KBs for edit mode
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        const jwtToken = Cookies.getJwtToken();
        let kbs = [];
        if (mode === 'edit') {
          // Always fetch all KBs for edit mode
          const res = await axios.post(`${API_URL}/kbs/getAllKbs`, null, {
            headers: { Authorization: `Bearer ${jwtToken}` }
          });
          kbs = res.data || [];
          // Fetch attached KBs for this Q&A
          if (qaData?.id) {
            const res2 = await axios.get(`${API_URL}/qa/kbIds/${qaData.id}`);
            const preselectedKbIds = Array.isArray(res2.data) ? res2.data : [];
            setSelectedKbIds(preselectedKbIds);
          }
        } else if (showAllKbs) {
          const res = await axios.post(`${API_URL}/kbs/getAllKbs`, null, {
            headers: { Authorization: `Bearer ${jwtToken}` }
          });
          kbs = res.data || [];
        } else if (selectedAgentId) {
          const res = await axios.post(`${API_URL}/agents/getAgentKbs`, { agentId: selectedAgentId }, {
            headers: { Authorization: `Bearer ${jwtToken}` }
          });
          kbs = res.data || [];
        }
        setKbOptions(kbs);
        setFilteredKbOptions(kbs);
      } catch {
        setKbOptions([]);
        setFilteredKbOptions([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [showAllKbs, selectedAgentId, open, mode, qaData]);

  // KB selection logic
  const allKbIds = filteredKbOptions.map(kb => kb.id);
  const allSelectedLocal = allKbIds.length > 0 && allKbIds.every(id => selectedKbIds.includes(id));
  const handleKbToggleLocal = (id) => {
    setSelectedKbIds(prev => prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]);
  };
  const handleSelectAllKbsLocal = () => {
    setSelectedKbIds(allSelectedLocal ? [] : allKbIds);
  };

  // Ensure all anchor tags have target="_blank"
  const ensureLinksHaveTargetBlank = (html) => {
    if (!html) return html;
    // Process anchor tags to ensure they have target="_blank"
    return html.replace(/<a\s+([^>]*)>/gi, (match, attributes) => {
      // Check if target="_blank" already exists
      const hasTarget = /target\s*=\s*["']_blank["']/i.test(attributes);

      if (hasTarget) {
        return match; // Already has target="_blank", return as is
      }

      // Add target="_blank" to the attributes
      const newAttributes = attributes.trim() + ' target="_blank"';
      return `<a ${newAttributes}>`;
    });
  };

  const handleSave = () => {
    // Process answer to ensure all links have target="_blank"
    const processedAnswer = ensureLinksHaveTargetBlank(answer);
    onSave({ ...qaData, question, answer: processedAnswer, agentId: selectedAgentId, kbIds: selectedKbIds });
  };

  // Map modal modes to descriptive headings
  const titleMap = {
    create: 'Train Knowledge - Create a New Q&A',
    answer: 'Train Unanswered Question - Create a New Q&A',
    edit: 'Train Q&A - Edit Existing Q&A'
  };
  const modalTitle = titleMap[mode] || 'Train Q&A';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{modalTitle}</DialogTitle>
      <DialogContent sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 180px)' }}>
        <TextField
          label="Question"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          fullWidth
          sx={{ mb: 2, mt: 2 }}
          disabled={mode === 'answer' && !!qaData?.question}
        />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Answer</Typography>
        <Box sx={{ ...wysiwygEditorBoxSx, minHeight: 280, '& .rsw-editor, & .rsw-content, & [contenteditable="true"]': { minHeight: 140, maxHeight: 400 } }}>
          <CustomizationRichTextEditor
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Type your answer here..."
            enableImageTools
          />
        </Box>
        {(mode === 'answer' || mode === 'edit' || mode === 'create') && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Agent:</Typography>
            <Select
              value={selectedAgentId}
              onChange={e => setSelectedAgentId(e.target.value)}
              displayEmpty
              fullWidth
              sx={{ mb: 1 }}
              disabled={!!qaData?.agentId}
            >
              <MenuItem value="">All Agents</MenuItem>
              {agents.map(a => (
                <MenuItem key={a.id} value={a.id}>{a.agentName}</MenuItem>
              ))}
            </Select>
            <FormControlLabel
              control={<Checkbox checked={showAllKbs} onChange={e => setShowAllKbs(e.target.checked)} />}
              label="Show all KBs"
            />
          </Box>
        )}
        {filteredKbOptions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Attach to Knowledge Base(s):</Typography>
            <FormGroup row>
              <FormControlLabel
                control={<Checkbox checked={allSelectedLocal} onChange={handleSelectAllKbsLocal} />}
                label="Select All"
              />
              {filteredKbOptions.map(kb => (
                <FormControlLabel
                  key={kb.id}
                  control={<Checkbox checked={selectedKbIds.includes(kb.id)} onChange={() => handleKbToggleLocal(kb.id)} />}
                  label={kb.kbName}
                />
              ))}
            </FormGroup>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !(selectedKbIds && selectedKbIds.length > 0)}
        >
          {mode === 'edit' ? 'Save' : 'Add to Q&A'}
        </Button>
      </DialogActions>
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(255,255,255,0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(3px)'
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}
    </Dialog>
  );
}
